import React from 'react';
import { Sparkles } from 'lucide-react';

export interface PageProgress {
  status: string;
  current?: number;
  total?: number;
  detections?: number;
}

export interface AutocountProgressOverlayProps {
  show: boolean;
  autocountProgress: string;
  pageProgressMap: Map<number, PageProgress>;
}

export const AutocountProgressOverlay: React.FC<AutocountProgressOverlayProps> = ({
  show,
  autocountProgress,
  pageProgressMap,
}) => {
  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      background: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      padding: '32px 40px',
      borderRadius: '16px',
      fontSize: '16px',
      fontWeight: 500,
      boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
      zIndex: 100003,
      minWidth: '480px',
      maxWidth: '600px',
      maxHeight: '80vh',
      overflowY: 'auto',
      textAlign: 'center',
      border: '1px solid var(--border-color)'
    }}>
      <div style={{ marginBottom: '20px' }}>
        <Sparkles
          size={48}
          style={{
            color: '#3b82f6',
            animation: 'pulse 1.5s ease-in-out infinite',
            margin: '0 auto',
            display: 'block'
          }}
        />
      </div>
      <div style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
        {autocountProgress}
      </div>

      {/* Page Progress Details */}
      {pageProgressMap.size > 0 && (
        <div style={{
          marginTop: '24px',
          maxHeight: '300px',
          overflowY: 'auto',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          padding: '16px',
          textAlign: 'left'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 600,
            color: 'var(--text-muted)',
            marginBottom: '12px',
            textAlign: 'center'
          }}>
            Page Progress ({Array.from(pageProgressMap.values()).filter(p => p.status === 'complete').length}/{pageProgressMap.size} complete)
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '8px'
          }}>
            {Array.from(pageProgressMap.entries())
              .sort(([a], [b]) => a - b)
              .map(([page, progress]) => (
                <div
                  key={page}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: progress.status === 'complete' ? 'var(--green-tint-strong)' :
                      progress.status === 'error' ? 'var(--red-tint-medium)' : 'var(--bg-primary)',
                    borderRadius: '6px',
                    fontSize: '12px',
                    border: '1px solid ' + (progress.status === 'complete' ? '#6ee7b7' :
                      progress.status === 'error' ? '#fca5a5' : 'var(--border-color)')
                  }}
                >
                  <span style={{ fontWeight: 600 }}>Page {page}</span>
                  <span style={{ color: 'var(--text-muted)', fontSize: '11px' }}>
                    {progress.status === 'complete' ? (
                      `✓ ${progress.detections} found`
                    ) : progress.status === 'error' ? (
                      '✗ Error'
                    ) : (
                      `${progress.current}/${progress.total}`
                    )}
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
